import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getCoperReservationList, reExportCoper } from '../../../../api/endpoints/coper';
import type { CoperReservationList } from '../../../../api/types/coper';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { CoperReservationDetailDialog } from '../components/CoperReservationDetailDialog';

const money = (n?: number | null) =>
  n == null ? '—' : n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const day = (s?: string | null) => (s && dayjs(s).isValid() ? dayjs(s).format('DD.MM.YYYY') : s || '—');
const pnrText = (n?: number | null) => (n == null ? '' : String(n));

/**
 * "Coper Rezervasyonları" (Coper B2B) — SA-6.
 * - Form: firma no (`companyNumber`, zorunlu) + tarih aralığı + opsiyonel para birimi →
 *   `Hotel/ReservationList` → düz MUI tablo.
 * - Satıra tıkla → `Hotel/ReservationLoad` detay diyaloğu.
 * - "Yeniden Export" (satır ikonu veya detay) → `Hotel/ReExportCoper` (Hangfire job id
 *   diyalogda elle girilir — bu uçlar job id'yi ne listede ne detayda döndürüyor).
 */
export default function CoperReservationsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [companyNumber, setCompanyNumber] = useState('');
  const [start, setStart] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [end, setEnd] = useState<Dayjs | null>(dayjs());
  const [currencyType, setCurrencyType] = useState('');

  const [detailPnr, setDetailPnr] = useState<string | null>(null);
  const [reExportOpen, setReExportOpen] = useState(false);
  const [jobId, setJobId] = useState('');

  const list = useMutation({
    mutationFn: () =>
      getCoperReservationList({
        companyNumber: Number(companyNumber),
        startDate: (start ?? dayjs()).startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        endDate: (end ?? dayjs()).endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        currencyType: currencyType.trim() || undefined,
      }),
  });

  const reExport = useMutation({
    mutationFn: (id: string) => reExportCoper(id.trim()),
    onSuccess: (res) => {
      enqueueSnackbar(
        res.result ? 'Yeniden export başlatıldı.' : 'İstek gönderildi ancak sonuç olumsuz döndü.',
        { variant: res.result ? 'success' : 'warning' }
      );
      setReExportOpen(false);
      setJobId('');
    },
    onError: (err) =>
      enqueueSnackbar(err instanceof Error ? err.message : 'Yeniden export başarısız.', {
        variant: 'error',
      }),
  });

  const rows: CoperReservationList[] = list.data?.coperReservationList ?? [];
  const companyValid = companyNumber.trim() !== '' && Number.isFinite(Number(companyNumber));
  const rangeValid =
    !!start && !!end && start.isValid() && end.isValid() && end.diff(start, 'day') >= 0;

  const openReExport = () => {
    setJobId('');
    setReExportOpen(true);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Coper Rezervasyonları
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ flexWrap: 'wrap', alignItems: { md: 'center' } }}
        >
          <TextField
            size="small"
            label="Firma No"
            value={companyNumber}
            onChange={(e) => setCompanyNumber(e.target.value.replace(/[^0-9]/g, ''))}
            sx={{ width: 140 }}
          />
          <DatePicker label="Başlangıç" value={start} onChange={setStart} slotProps={{ textField: { size: 'small' } }} />
          <DatePicker label="Bitiş" value={end} onChange={setEnd} slotProps={{ textField: { size: 'small' } }} />
          <TextField
            size="small"
            label="Para Birimi (opsiyonel)"
            value={currencyType}
            onChange={(e) => setCurrencyType(e.target.value.toUpperCase())}
            sx={{ width: 200 }}
          />
          <Button
            variant="contained"
            onClick={() => list.mutate()}
            disabled={list.isPending || !companyValid || !rangeValid}
          >
            {list.isPending ? 'Getiriliyor…' : 'Listele'}
          </Button>
        </Stack>
        <ApiErrorAlert error={list.error} sx={{ mt: 2 }} />
      </Paper>

      {list.isPending && <CircularProgress size={24} />}

      {list.isSuccess && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {rows.length} rezervasyon
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>PNR</TableCell>
                  <TableCell>Otel</TableCell>
                  <TableCell>Misafir</TableCell>
                  <TableCell>Giriş</TableCell>
                  <TableCell>Çıkış</TableCell>
                  <TableCell align="right">Satış</TableCell>
                  <TableCell align="right">Bakiye</TableCell>
                  <TableCell>Para</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Kayıt</TableCell>
                  <TableCell align="right">İşlem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        Bu firma ve tarih aralığında rezervasyon bulunamadı.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row, idx) => (
                  <TableRow
                    key={pnrText(row.pNR_No) || row.shoppingCartId || idx}
                    hover
                    onClick={() => row.pNR_No != null && setDetailPnr(pnrText(row.pNR_No))}
                    sx={{ cursor: row.pNR_No != null ? 'pointer' : 'default' }}
                  >
                    <TableCell>{pnrText(row.pNR_No) || '—'}</TableCell>
                    <TableCell>{row.hotelName ?? '—'}</TableCell>
                    <TableCell>{row.name ?? '—'}</TableCell>
                    <TableCell>{day(row.startDate)}</TableCell>
                    <TableCell>{day(row.endDate)}</TableCell>
                    <TableCell align="right">{money(row.salesPrice)}</TableCell>
                    <TableCell align="right">{money(row.balancePrice)}</TableCell>
                    <TableCell>{row.currencyCode ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.deleted ? 'İptal' : row.prebooked ? 'Ön Rezerve' : 'Aktif'}
                        color={row.deleted ? 'default' : row.prebooked ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell>{day(row.saveDate)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        title="Yeniden Export"
                        onClick={(e) => {
                          e.stopPropagation();
                          openReExport();
                        }}
                      >
                        <ReplayOutlinedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <CoperReservationDetailDialog
        pnrNo={detailPnr}
        onClose={() => setDetailPnr(null)}
        onReExport={openReExport}
      />

      <Dialog open={reExportOpen} onClose={() => !reExport.isPending && setReExportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Yeniden Export</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Coper export'unu yeniden çalıştırmak için Hangfire job kimliğini girin.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Job ID"
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReExportOpen(false)} disabled={reExport.isPending}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            onClick={() => reExport.mutate(jobId)}
            disabled={reExport.isPending || jobId.trim() === ''}
          >
            {reExport.isPending ? 'Gönderiliyor…' : 'Yeniden Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
